import React, { useState, useEffect } from 'react';
import { Award, Calendar, Building2, ArrowRight, Loader2, Download } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useSettings } from '../context/SettingsContext';
import api from '../utils/api';

const STATUS_STYLE = { open:'bg-emerald-100 text-emerald-700', upcoming:'bg-amber-100 text-amber-700', closed:'bg-gray-100 text-gray-500' };

export default function GrantsPage() {
  const { s } = useSettings();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus]   = useState('');

  useEffect(() => {
    api.get(`/grants?limit=50${status?`&status=${status}`:''}`).then(r=>setGrants(r.data.items||[])).catch(()=>setGrants([])).finally(()=>setLoading(false));
  }, [status]);

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">
        <section className="relative bg-hero-gradient py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">Funding Opportunities</span>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">Grants & Funding</h1>
            <p className="text-white/60 text-lg max-w-xl mx-auto">Explore available grants and funding opportunities supported by or accessible through SAAI Institute.</p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex gap-2 justify-center mb-8">
              {[['','All'],['open','Open'],['upcoming','Upcoming'],['closed','Closed']].map(([v,l])=><button key={v} onClick={()=>setStatus(v)} className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all ${status===v?'bg-royal-800 border-royal-800 text-white':'border-gray-200 text-gray-600 hover:border-royal-300 bg-white'}`}>{l}</button>)}
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-48 gap-2 text-gray-400"><Loader2 size={22} className="animate-spin"/>Loading...</div>
            ) : grants.length === 0 ? (
              <div className="text-center py-20 text-gray-400"><Award size={40} className="mx-auto mb-3 opacity-30"/><p>No grants found.</p></div>
            ) : (
              <div className="space-y-4">
                {grants.map(g => {
                  const days = g.deadline ? Math.ceil((new Date(g.deadline)-new Date())/86400000) : null;
                  return (
                    <div key={g._id} className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${STATUS_STYLE[g.status]||STATUS_STYLE.closed}`}>{g.status}</span>
                            {g.category && <span className="px-3 py-1 bg-royal-50 text-royal-700 rounded-full text-xs font-semibold capitalize">{g.category}</span>}
                            {days!==null && g.status==='open' && days>0 && <span className={`px-3 py-1 rounded-full text-xs font-semibold ${days<14?'bg-red-50 text-red-600':'bg-gray-100 text-gray-500'}`}>⏰ {days} days left</span>}
                          </div>
                          <h3 className="font-display font-semibold text-gray-900 text-lg mb-1">{g.title}</h3>
                          {(g.funder||g.donor) && <div className="flex items-center gap-1.5 text-royal-800 font-semibold text-sm mb-3"><Building2 size={14}/>{g.funder||g.donor}</div>}
                          <p className="text-gray-500 text-sm leading-relaxed">{g.description}</p>
                          {g.deadline && <div className="flex items-center gap-2 mt-3 text-xs text-gray-400"><Calendar size={13}/>Deadline: <strong className="text-gray-700">{new Date(g.deadline).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'})}</strong></div>}
                        </div>
                        <div className="sm:text-right flex-shrink-0">
                          {g.amount && <div className="mb-4"><div className="text-xs text-gray-400 mb-0.5">Grant Amount</div><div className="font-display text-2xl font-bold text-royal-800">{g.currency || ''}{Number(g.amount).toLocaleString()}</div></div>}
                          {g.reportUrl && <a href={g.reportUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-royal-800 font-semibold hover:underline mb-2"><Download size={13}/>Guidelines</a>}
                          {g.status==='open'
                            ? <a href={`mailto:${s('contact_email','grants@saaingo.org')}?subject=Grant Application: ${g.title}`} className="btn-royal text-sm py-2 px-4">Apply Now <ArrowRight size={14}/></a>
                            : <button disabled className="px-4 py-2 bg-gray-100 text-gray-400 rounded-xl text-sm font-semibold cursor-not-allowed">Closed</button>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}